import type { BlogPost } from './types'
import type { SupportedLanguage } from '../../i18n'
import { WIR_SIND_LIVE } from './wir-sind-live'
import { WARUM_KEINE_EMAIL_REGISTRIERUNG } from './warum-keine-email-registrierung'
import { PHOTOVOLTAIK_FOERDERUNG_2026 } from './photovoltaik-foerderung-2026'
import { PHOTOVOLTAIK_WAERMEPUMPE_KOMBINIEREN } from './photovoltaik-waermepumpe-kombinieren'
import { JESTESMY_ONLINE } from './pl/jestesmy-online'
import { DLACZEGO_BEZ_REJESTRACJI_EMAIL } from './pl/dlaczego-bez-rejestracji-email'
import { PIEC_BLEDOW_PRZY_ZBIERANIU_OFERT_FOTOWOLTAICZNYCH } from './pl/5-bledow-przy-zbieraniu-ofert-fotowoltaicznych'
// SEO-4.0-Story Task 01: ES/PL translations of the Wärmepumpe blog post - had no ES/PL siblings before.
import { LACZENIE_FOTOWOLTAIKI_Z_POMPA_CIEPLA } from './pl/laczenie-fotowoltaiki-z-pompa-ciepla'
// SEO-4.0-Story Task 07 (docs/tasks/active/seo/seo-4.0/07-verbrauchsstarke-nischen-nachts-autark.md):
// KI-Server-Stromverbrauch - praktisch unkontestiertes Whitespace-Thema, neuer 3-sprachiger Artikel.
import { LOKALE_KI_MODELLE_PHOTOVOLTAIK } from './lokale-ki-modelle-photovoltaik'
import { LOKALNE_MODELE_AI_FOTOWOLTAIKA } from './pl/lokalne-modele-ai-fotowoltaika'
import { YA_ESTAMOS_EN_MARCHA } from './es/ya-estamos-en-marcha'
import { POR_QUE_SIN_REGISTRO_POR_EMAIL } from './es/por-que-sin-registro-por-email'
import { CINCO_ERRORES_AL_PEDIR_PRESUPUESTOS_SOLARES } from './es/5-errores-al-pedir-presupuestos-solares'
import { FOTOVOLTAICA_Y_BOMBA_DE_CALOR_COMBINADAS } from './es/fotovoltaica-y-bomba-de-calor-combinadas'
import { MODELOS_IA_LOCALES_FOTOVOLTAICA } from './es/modelos-ia-locales-fotovoltaica'

export type { BlogPost, BlogSection } from './types'
export {
  WIR_SIND_LIVE,
  WARUM_KEINE_EMAIL_REGISTRIERUNG,
  PHOTOVOLTAIK_FOERDERUNG_2026,
  PHOTOVOLTAIK_WAERMEPUMPE_KOMBINIEREN,
  JESTESMY_ONLINE,
  DLACZEGO_BEZ_REJESTRACJI_EMAIL,
  PIEC_BLEDOW_PRZY_ZBIERANIU_OFERT_FOTOWOLTAICZNYCH,
  LACZENIE_FOTOWOLTAIKI_Z_POMPA_CIEPLA,
  LOKALE_KI_MODELLE_PHOTOVOLTAIK,
  LOKALNE_MODELE_AI_FOTOWOLTAIKA,
  YA_ESTAMOS_EN_MARCHA,
  POR_QUE_SIN_REGISTRO_POR_EMAIL,
  CINCO_ERRORES_AL_PEDIR_PRESUPUESTOS_SOLARES,
  FOTOVOLTAICA_Y_BOMBA_DE_CALOR_COMBINADAS,
  MODELOS_IA_LOCALES_FOTOVOLTAICA,
}

/** All blog posts across all languages - see marketing/seo/blog.txt for the planned topic
 *  backlog. Not every topicId has a post in every language: some posts are translations of each
 *  other (grouped by topicId), others are written natively for a single market and have no
 *  siblings (see PIEC_BLEDOW_PRZY_ZBIERANIU_OFERT_FOTOWOLTAICZNYCH / CINCO_ERRORES_...). */
export const BLOG_POSTS: BlogPost[] = [
  WIR_SIND_LIVE,
  WARUM_KEINE_EMAIL_REGISTRIERUNG,
  PHOTOVOLTAIK_FOERDERUNG_2026,
  PHOTOVOLTAIK_WAERMEPUMPE_KOMBINIEREN,
  JESTESMY_ONLINE,
  DLACZEGO_BEZ_REJESTRACJI_EMAIL,
  PIEC_BLEDOW_PRZY_ZBIERANIU_OFERT_FOTOWOLTAICZNYCH,
  LACZENIE_FOTOWOLTAIKI_Z_POMPA_CIEPLA,
  LOKALE_KI_MODELLE_PHOTOVOLTAIK,
  LOKALNE_MODELE_AI_FOTOWOLTAIKA,
  YA_ESTAMOS_EN_MARCHA,
  POR_QUE_SIN_REGISTRO_POR_EMAIL,
  CINCO_ERRORES_AL_PEDIR_PRESUPUESTOS_SOLARES,
  FOTOVOLTAICA_Y_BOMBA_DE_CALOR_COMBINADAS,
  MODELOS_IA_LOCALES_FOTOVOLTAICA,
]

export function getBlogPostBySlug(slug: string, lang: SupportedLanguage = 'de'): BlogPost | undefined {
  return BLOG_POSTS.find((post) => post.slug === slug && post.lang === lang)
}

/** Posts for one language, newest first - used by BlogListPage. */
export function getBlogPostsByLang(lang: SupportedLanguage): BlogPost[] {
  return BLOG_POSTS.filter((post) => post.lang === lang).sort((a, b) => b.publishedAt.localeCompare(a.publishedAt))
}

/** All language variants of the same article, e.g. the launch post in DE/PL/ES - used to build
 *  the hreflang cluster for a given post (see useBlogSeo.ts). */
export function getBlogPostsByTopic(topicId: string): BlogPost[] {
  return BLOG_POSTS.filter((post) => post.topicId === topicId)
}

/** The sibling of `post` in `targetLang`, if a translation exists - used by the language switcher
 *  to jump to the equivalent article instead of just the target language's blog index. */
export function getBlogPostSibling(post: BlogPost, targetLang: SupportedLanguage): BlogPost | undefined {
  return BLOG_POSTS.find((p) => p.topicId === post.topicId && p.lang === targetLang)
}

/** DE keeps the original bare /blog URL; PL/ES use a /pl or /es prefix (see App.tsx) - unlike the
 *  SEO landing pages, the blog index itself isn't a keyword-targeted page, so a lang-prefixed path
 *  is simpler here than inventing a per-language index slug. */
export function blogListPath(lang: SupportedLanguage): string {
  return lang === 'de' ? '/blog' : `/${lang}/blog`
}

export function blogPostPath(post: BlogPost): string {
  return post.lang === 'de' ? `/blog/${post.slug}` : `/${post.lang}/blog/${post.slug}`
}

/** Parses a pathname into its blog-route language/slug, or null if it isn't a blog route at all -
 *  used by LanguageSwitcher to detect when it's on a blog page and jump to the right sibling. */
export function parseBlogPath(pathname: string): { lang: SupportedLanguage; slug?: string } | null {
  const match = pathname.match(/^\/(?:(pl|es)\/)?blog(?:\/([^/]+))?\/?$/)
  if (!match) return null
  return { lang: (match[1] as SupportedLanguage | undefined) ?? 'de', slug: match[2] }
}
